import React, {Fragment} from 'react';
import {Divider, Stack, Typography, useTheme} from '@mui/material';

import {AccordionData, HighlightedText, StyledGrid} from 'presentation/components';
import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {ServiceCard} from '../ServiceCard';
import {ServiceCategoryProps} from './types';
import {CustomAccordionDetails} from './styles';
import Icon from 'presentation/icons';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';

export function ServiceCategory({serviceCategory}: ServiceCategoryProps) {
	const {isMobile} = useMediaQueries();
	const theme = useTheme();
	const showCategory = serviceCategory && serviceCategory.list?.length > 0;
	const {SEARCH_PARAMS} = useQueryParams();
	const {query} = SEARCH_PARAMS;
	const {isEnabled: isAccordionServicesFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_ACCORDION_SERVICES,
	);

	const getServicesList = () => {
		const GridItemWrapper =
			isAccordionServicesFeatureFlagEnabled && !isMobile ? StyledGrid.Item : React.Fragment;

		const cards = serviceCategory.list.map(item => (
			<GridItemWrapper key={item.title} {...(!isMobile && {sm: 6, lg: 4})}>
				<ServiceCard
					icon={item.icon}
					imageSrc={item.imageSrc}
					title={item.title}
					type={item.type}
					route={item.route}
					endpoint={item.endpoint}
					disableCache={item.disableCache}
					openInIframe={item.openInIframe}
					externalLink={item.externalLink}
					featureFlagName={item.featureFlag}
					isBlob={item.isBlob}
					dataTagId={item?.dataTagId}
					routeReplace={false}
					handleClick={item?.handleClick}
				/>
			</GridItemWrapper>
		));

		if (isMobile && isAccordionServicesFeatureFlagEnabled) {
			const accordionKey = serviceCategory.category + (query ?? '');

			return (
				<AccordionData
					key={accordionKey}
					title={<HighlightedText text={serviceCategory.category} highlight={query} />}
					expandIcon={
						<Icon
							name="expand-more-filled"
							color={theme.colors.light.text.secondary}
							size={24}
						/>
					}
					defaultExpanded
					disableGutters
					sx={{
						'&:before': {
							display: 'none',
						},
					}}>
					<CustomAccordionDetails>
						<Divider />
						{cards}
					</CustomAccordionDetails>
				</AccordionData>
			);
		}

		return (
			<Stack gap={1}>
				{serviceCategory?.category && (
					<Typography variant="subheader">
						<HighlightedText text={serviceCategory.category} highlight={query} />
					</Typography>
				)}
				{isAccordionServicesFeatureFlagEnabled ? (
					<StyledGrid.Root>{cards}</StyledGrid.Root>
				) : (
					cards
				)}
			</Stack>
		);
	};

	return <Fragment>{showCategory ? getServicesList() : null}</Fragment>;
}
